import { CITY } from '@city'
/**
 * StationStatusController — owns the StationStatus preloader + IndexedDB
 * cache and feeds live snapshots into React context (spec §7, task 3.7).
 *
 * Mount at app root (inside AppShell, once). It:
 *  - Creates the cache (IndexedDB with in-memory fallback) + preloader.
 *  - On mount: subscribes to cache.updates() BEFORE calling refresh() so
 *    warm-launch data paints before any network call completes.
 *  - Calls refresh() on cold start.
 *  - Calls start() to begin the 60 s poll loop.
 *  - Pauses the loop on `visibilitychange` hidden; resumes on visible
 *    and immediately refreshes (foreground re-entry per spec §5).
 *  - On unmount: stops the preloader + unsubscribes.
 *
 * The snapshot is published into `<StationStatusContext.Provider>` so any
 * descendant can read it via `useStationStatuses()` without prop drilling.
 */
import { useEffect, useState, type ReactNode } from 'react'
import {
  createStationStatusClient,
  createStationStatusPreloader,
  IndexedDbStationStatusCache,
  InMemoryStationStatusCache,
} from '@metro/station-status'
import type { StationStatusSnapshot } from '@metro/station-status'
import { StationStatusContext } from '../state/stationStatusContext'
import { API_PREFIX } from '../schema/mapSet'
import { reportHealthError, classifyNetworkError, HEALTH_ID } from '../analytics/appHealthEvents'

/** Base URL for the status endpoint. */
const STATUS_BASE_URL = API_PREFIX

function createCache() {
  // Fall back to InMemory when IndexedDB is unavailable (SSR / older browsers).
  if (typeof indexedDB === 'undefined') return new InMemoryStationStatusCache()
  // v2 snapshots carry v2 line ids: a fresh store, so a v1 snapshot cached
  // before the switch never paints a row (TASK-986).
  return new IndexedDbStationStatusCache('metro-station-status-v2')
}

interface StationStatusControllerProps {
  children: ReactNode
  /**
   * Poll live station status. Default: the city has one (`CITY.stationStatus`);
   * a city without it publishes an empty snapshot and makes no request.
   */
  live?: boolean
}

/**
 * Provider that owns the StationStatusPreloader lifecycle.
 * Mount once at the AppShell level, above any consumer of `useStationStatuses`.
 */
export function StationStatusController({
  children,
  live = CITY.stationStatus,
}: StationStatusControllerProps): React.ReactElement {
  const [statuses, setStatuses] = useState<StationStatusSnapshot>({})

  useEffect(() => {
    if (!live) return
    const cache = createCache()
    const client = createStationStatusClient(STATUS_BASE_URL)
    const preloader = createStationStatusPreloader({
      client,
      cache,
      // §3/§4 `api_request_failed` (endpoint=status, fallback=cache) — a
      // status refresh failed and we are now serving the STALE cache. This is
      // the silent-fallback "warning" the health contract calls the gold
      // signal: a spike here means live status has degraded to stale for users
      // before anyone notices.
      onFetchError: (error) => {
        const { errorKind, statusCode } = classifyNetworkError(error)
        reportHealthError(HEALTH_ID.apiRequestFailed, `status refresh failed: ${error.message}`, {
          domain: 'network',
          severity: 'warning',
          errorKind,
          endpoint: 'status',
          statusCode,
          fallback: 'cache',
        })
      },
    })

    // Subscribe BEFORE the first refresh so warm-launch cached data lands
    // in state before the network call completes.
    const unsubscribe = cache.updates((snapshot) => setStatuses(snapshot))

    // Cold-start refresh + start the 60 s loop.
    void preloader.refresh()
    preloader.start()

    // Pause loop on background, refresh + resume on foreground.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        preloader.start()
        void preloader.refresh()
      } else {
        preloader.stop()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      preloader.stop()
      unsubscribe()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [live])

  return <StationStatusContext.Provider value={statuses}>{children}</StationStatusContext.Provider>
}
